import React, { useState, useEffect } from "react";
import { Link, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import Seo from "../../shared/seo/Seo";
import { urls, copy, currentLang } from "../../shared/seo/links";
import useCanonical from "../../shared/seo/useCanonical";
import PathLink from "../../shared/seo/PathLink";
import PageContent from "../../components/questions/PageContent";
import QuestionBody from "../../components/questions/QuestionBody";
import QuestionCommentSection from "../../components/questions/QuestionCommentSection";
import QuestionLawyerAnswer from "../../components/questions/QuestionLawyerAnswer";
import QuestionMainSection from "../../components/questions/QuestionMainSection";
import EditorialAnswer from "../../components/questions/EditorialAnswer";
import SideBar from "../../components/questions/SideBar";
import { askQuestionUrl, currentUser } from "../../components/questions/questionHelpers";
import RelatedBlock from "../../components/content/RelatedBlock";
import SourcesBlock from "../../components/content/SourcesBlock";
import { formatArticleDate } from "../../components/blogs/articleHelpers";
import { citationLd, lastUpdated } from "@seo/sources";
import FindLawyerBlock from "../../components/content/FindLawyerBlock";
import { breadcrumbJsonLd } from "../../components/content/contentLinks";
import ComponentsLoader from "../../components/includes/ComponentsLoader";
import NotFound from "../NotFound/NotFound";
import { useQuestionContext } from "../../shared/context/QuestionContext";

/**
 * Structured data that says who answered. A visitor's question answered by
 * lawyers is a QAPage; an editorial FAQ is written and answered by the site
 * itself, so it is an FAQPage whose answer's author is the Organization.
 */
export function questionJsonLd(question, canonical) {
  const url = urls.absoluteUrl(window.location.origin, canonical);
  const answers = question.answers || [];
  if (question.editorial) {
    const answer = answers.find((a) => a.editorial);
    if (!answer) return null;
    const citation = citationLd(question.sources);
    const updated = lastUpdated(question);
    return {
      "@context": "https://schema.org",
      "@type": "FAQPage",
      inLanguage: urls.questionLang(question),
      url,
      ...(updated ? { dateModified: updated.toISOString() } : {}),
      ...(citation.length ? { citation } : {}),
      mainEntity: [
        {
          "@type": "Question",
          name: question.title,
          text: question.content || question.title,
          dateCreated: question.createdAt,
          acceptedAnswer: {
            "@type": "Answer",
            text: copy.stripHtml(answer.answer),
            dateCreated: answer.createdAt,
            url: `${url}#answer-1`,
            author: { "@type": "Organization", name: copy.SITE_NAME, url: urls.absoluteUrl(window.location.origin, "/") },
          },
        },
      ],
    };
  }
  if (!answers.length) return null;
  return {
    "@context": "https://schema.org",
    "@type": "QAPage",
    mainEntity: {
      "@type": "Question",
      name: question.title,
      text: question.content || question.title,
      dateCreated: question.createdAt,
      answerCount: answers.length,
      suggestedAnswer: answers.map((a, i) => ({
        "@type": "Answer",
        text: copy.stripHtml(a.answer),
        dateCreated: a.createdAt,
        url: `${url}#answer-${i + 1}`,
      })),
    },
  };
}

export default function Question() {
  const { t } = useTranslation();
  const user = currentUser();
  const { getQuestionsById, getQuestionRelated } = useQuestionContext();
  const [question, setQuestion] = useState(null);
  const [related, setRelated] = useState({});
  const [notFound, setNotFound] = useState(false);
  // Bumped after an answer is posted, edited or deleted to reload the answers.
  const [version, setVersion] = useState(0);
  const { id } = useParams();
  useEffect(() => {
    async function fetchData() {
      let res = await getQuestionsById({ id: id });
      // axiosFactory returns the error message instead of data on a 404.
      if (res.data?.question) {
        setQuestion(res.data.question);
      } else {
        setNotFound(true);
      }
    }
    fetchData();
  }, [getQuestionsById, id, version]);
  useEffect(() => {
    let live = true;
    setRelated({});
    getQuestionRelated({ id }).then((res) => {
      if (live) setRelated(res?.data?.status === "success" ? res.data : {});
    });
    return () => {
      live = false;
    };
  }, [id]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [id]);
  const refresh = () => setVersion((v) => v + 1);
  // A question lives in its own language only; old or mistyped slugs move to it.
  const canonical = question ? urls.questionPath(question) : null;
  useCanonical(canonical);

  if (notFound) return <NotFound />;
  if (!question) {
    return (
      <PageContent>
        <Seo title={copy.questions(currentLang()).title} />
        <QuestionMainSection>
          <ComponentsLoader />
        </QuestionMainSection>
        <SideBar />
      </PageContent>
    );
  }

  const lang = urls.questionLang(question);
  const editorial = question.editorial === true;
  const answers = question.answers || [];
  const editorialAnswer = editorial ? answers.find((a) => a.editorial) : null;
  const category = editorial ? urls.findCategory(related.category || question.category) : null;
  const crumbs = [
    { name: t("Home"), path: urls.homePath(lang) },
    { name: copy.questions(lang).h1, path: urls.sectionPath("questions", lang) },
    ...(category ? [{ name: urls.categoryName(category, lang), path: urls.categoryPath(category, lang) }] : []),
    { name: question.title, path: canonical },
  ];
  const ld = questionJsonLd(question, canonical);
  // Only an FAQ that cites its sources was checked against the law: the
  // "Last updated" line goes with them.
  const updated = editorial && question.sources?.length ? lastUpdated(question) : null;

  return (
    <>
      <Seo
        title={copy.question(lang, question).title}
        description={copy.question(lang, question).description}
        path={canonical}
        type="article"
        jsonLd={[...(ld ? [ld] : []), breadcrumbJsonLd(crumbs)]}
      />
      <PageContent>
        <QuestionMainSection>
          <QuestionBody question={question} crumbs={crumbs} />
          {editorial ? (
            <>
              {editorialAnswer && (
                <>
                  <EditorialAnswer answer={editorialAnswer} lang={lang} />
                  <SourcesBlock sources={question.sources} />
                  {updated && (
                    <p className="avc-article__updated">
                      {t("article_last_updated")}
                      {lang === "fr" ? " : " : ": "}
                      <time dateTime={updated.toISOString()}>{formatArticleDate(updated, lang)}</time>
                    </p>
                  )}
                </>
              )}
              {(related.guide || category) && (
                <ul className="avc-editorial-links">
                  {related.guide && (
                    <li>
                      <PathLink to={urls.blogPath(related.guide)}>
                        <i className="far fa-file-alt" aria-hidden="true"></i>
                        <span className="avc-editorial-links__label">{t("faq_related_guide")}</span>
                        <span dir="auto">{related.guide.title}</span>
                      </PathLink>
                    </li>
                  )}
                  {category && (
                    <li>
                      <PathLink to={urls.categoryPath(category, lang)}>
                        <i className="fas fa-tag" aria-hidden="true"></i>
                        <span className="avc-editorial-links__label">{t("topic_label")}</span>
                        <span>{urls.categoryName(category, lang)}</span>
                      </PathLink>
                    </li>
                  )}
                </ul>
              )}
              <RelatedBlock items={related.faq} title={t("faq_more_in_topic")} />
              {user?.role !== "lawyer" && (
                <div className="avc-editorial-cta">
                  <p>{t("faq_ask_own_text")}</p>
                  <Link to={askQuestionUrl()} className="avc-btn avc-btn--gold">
                    <i className="far fa-edit" aria-hidden="true"></i>
                    {t("faq_ask_own")}
                  </Link>
                </div>
              )}
            </>
          ) : (
            <>
              <QuestionCommentSection answers={answers} onChange={refresh} />
              {user?.role === "lawyer" && user.lawyerID?.verified && (
                <QuestionLawyerAnswer id={question._id} onAnswered={refresh} />
              )}
            </>
          )}
          <FindLawyerBlock cities={related.cities} />
        </QuestionMainSection>
        <SideBar />
      </PageContent>
    </>
  );
}
